import { useMemo } from 'react';
import { useLibraryEffectiveWatermarkQuery } from '@/generated/graphql';
import { gqlToWatermarkConfig, type CampaignWatermarkGql, type WatermarkConfig } from '@/lib/campaign-watermark-types';

export type LibraryWatermarkState = {
  /** Resolved campaign watermark config when an active campaign has one, else null. */
  config: WatermarkConfig | null;
  /** True when at least one active CPM campaign covers the library — whether or
   *  not that campaign has its own watermark. Mirrors the backend's
   *  renderLibrary override which turns OFF the default Autoclipper watermark
   *  whenever the library is CPM-active. The preview must suppress the
   *  default in the same case or it'll over-render. */
  cpmActive: boolean;
};

const EMPTY_STATE: LibraryWatermarkState = { config: null, cpmActive: false };

/**
 * Returns the watermark state for a library_project preview: the resolved
 * campaign watermark config (or null) plus a `cpmActive` flag the caller
 * can use to suppress the default Autoclipper watermark when the library
 * is part of an active CPM campaign that hasn't configured a watermark.
 * When `libraryDocumentId` is falsy, the query is skipped and the empty
 * state is returned.
 */
export const useLibraryEffectiveWatermark = (
  libraryDocumentId: string | null | undefined,
): LibraryWatermarkState => {
  const { data } = useLibraryEffectiveWatermarkQuery({
    variables: { id: libraryDocumentId || '' },
    skip: !libraryDocumentId,
    fetchPolicy: 'cache-first',
  });

  return useMemo(() => {
    const library = data?.myLibrary;
    if (!library) return EMPTY_STATE;
    const wm = library.effective_watermark;
    return {
      config: wm ? gqlToWatermarkConfig(wm as CampaignWatermarkGql) : null,
      cpmActive: (library.active_cpm_campaigns?.length || 0) > 0,
    };
  }, [data?.myLibrary]);
};
